import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import {
	action,
	computed,
	makeObservable,
	observable,
	reaction,
	runInAction
} from 'mobx';
import { Auth, Amplify } from 'aws-amplify';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { Status } from 'app-types';
import {
	authGetTokenUrl,
	resolvePeakAuthEnvForToken
} from 'config/authHosts';
import {
	AuthUser,
	ResetPasswordRequest,
	ResetPasswordSubmitRequest,
	SignInRequest,
	CompleteNewPasswordRequest,
	ChangePasswordRequest,
	CurrentUser,
	GetCurrentUserResponse,
	ResetExpiredUserRequest
} from 'Services/Api/Auth/Types';
import { apolloClient, axiosClient } from 'Services/Api/client';
import { GET_CURRENT_USER } from 'Services/Api/Auth/Queries';
import { flattenDeep } from 'lodash';
import { BroadcastChannel } from 'broadcast-channel';
import AUTH_API from 'Services/Api/Auth/Api';
import COMMON_API from 'Services/Api/Common/Api';
import {
	UserPoolsResponse,
	TenantUserPoolType
} from 'Services/Api/Common/Types';
import { mutateAppRoutes, APP_ROUTES } from 'Pages/App/App.constants';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import { PermissionCode, TwoFactorAuthorizationStatus } from 'Services/Api/Users/Types';
import { GET_GROUPS_LIST } from 'Services/Api/Groups/Queries';
import { GetGroupsListRequest, GetGroupsListResponse } from 'Services/Api/Groups/Types';
import { DEFAULT_TABLE_REQUEST_VALUES } from 'Pages/Groups/Group/Group';
import { systemsStore } from './SystemsStore';
import { FEATURE_TYPE } from 'FeatureManager/FeatureManager.type';
import { FEATURE_PERMISSION_KEYS } from 'FeatureManager/FeatureManager.constant';

const BROADCAST_CHANNEL_MESSAGE = {
	SIGNOUT: 'signOut'
};

const TWO_FACTOR_PREFIX = '2FA_';

class AuthStore {
	channel: BroadcastChannel | null = null;
	userPools: UserPoolsResponse = [];
	currentUserPoolData: TenantUserPoolType | undefined;
	definedRoutePostfix: null | string = null;
	prevPostfix = '';
	status = {
		checkAuth: Status.Idle,
		getCurrentUser: Status.Idle
	};
	needReloadPostfix = false;
	authSession: CognitoUserSession | null = null;
	currentUser: CurrentUser | null = null;
	idleLogout = false;
	userPoolsStatus = Status.Idle;
	twoFactorAuthorizationPassed = false;
	justLoggedIn = false;
	// In-flight OAuth token fetch, so concurrent callers can await one ready
	// token instead of each racing an empty value. Not observable — it's
	// request-coordination state, not UI state.
	oauthTokenPromise: Promise<void> | null = null;

	get isAuthSessionValid() {
		return this.authSession && this.authSession.isValid();
	}

	get isAuthenticated() {
		return Boolean(
			this.isAuthSessionValid &&
				this.currentUser?.isConsentGiven &&
				this.isTwoFactorAuthorizationPassed
		);
	}

	get isTwoFactorAuthorizationEnabled() {
		return Boolean(
			this.currentUser?.primarySystemRole?.twoFactorAuthorizationEnabled
		);
	}

	get isTwoFactorAuthorizationPassed() {
		if (process.env.NODE_ENV === 'development') return true;

		return this.isTwoFactorAuthorizationEnabled
			? this.currentUser?.twoFactorAuthorizationStatus ===
					TwoFactorAuthorizationStatus.Setup &&
					this.twoFactorAuthorizationPassed
			: this.currentUser?.primarySystemRole
					?.twoFactorAuthorizationEnabled === false;
	}

	get isTwoFactorAuthorizationRequired() {
		return (
			this.isAuthSessionValid &&
			this.isTwoFactorAuthorizationEnabled &&
			this.currentUser?.twoFactorAuthorizationStatus !==
				TwoFactorAuthorizationStatus.Blocked
		);
	}

	get isUsersPoolDefined() {
		return this.currentUserPoolData !== undefined;
	}

	get currentUserPermissionCodes() {
		if (!this.currentUser) {
			return [];
		}
		const permissions = flattenDeep([
			this.currentUser.primarySystemRole?.permissions ?? [],
			(this.currentUser.secondarySystemRoles ?? []).map(
				(role) => role.permissions ?? []
			)
		]);
		return permissions.map((permission) => permission.code);
	}

	get isCurrentUserSuperAdmin() {
		return (
			this.currentUser?.primarySystemRole.role ===
			SystemRoleType.SuperAdmin
		);
	}

	get isCurrentUserOwner() {
		return (
			this.currentUser?.primarySystemRole.role === SystemRoleType.Owner
		);
	}

	get hasTenantAccess() {
		return (
			this.currentUser?.primarySystemRole.role === SystemRoleType.SuperAdmin ||
			this.currentUser?.isTenantRole === true
		);
	}

	constructor() {
		makeObservable(this, {
			status: observable,
			idleLogout: observable,
			currentUser: observable,
			authSession: observable,
			currentUserPoolData: observable,
			definedRoutePostfix: observable,
			needReloadPostfix: observable,
			prevPostfix: observable,
			userPoolsStatus: observable,
			twoFactorAuthorizationPassed: observable,
			justLoggedIn: observable,
			isAuthenticated: computed,
			isUsersPoolDefined: computed,
			currentUserPermissionCodes: computed,
			isCurrentUserSuperAdmin: computed,
			isCurrentUserOwner: computed,
			isTwoFactorAuthorizationPassed: computed,
			isTwoFactorAuthorizationEnabled: computed,
			isAuthSessionValid: computed,
			isTwoFactorAuthorizationRequired: computed
		});

		reaction(
			() => this.isAuthenticated,
			(isAuthenticated) => {
				if (isAuthenticated && !this.channel) {
					this.channel = new BroadcastChannel('auth');
					this.channel.onmessage = (ev) => {
						if (ev === BROADCAST_CHANNEL_MESSAGE.SIGNOUT) {
							this.signOut(false);
						}
					};
				}
			}
		);

		reaction(
			() => systemsStore.featureList,
			this._filterUserPermission
		);
	}

	@action
	public async getUsersPools(location: string) {
		try {
			const url = new URL(location);
			const [postfix] = url.pathname.split('/').filter(Boolean);
			const { data } = await axiosClient.get<UserPoolsResponse>(
				COMMON_API.USERS_POOLS
			);

			this.userPools = data;

			const currentUserPool = data.find(
				(pools) => pools.postfix === postfix
			);

			this.setupUserPool(currentUserPool);
			runInAction(() => {
				this.userPoolsStatus = Status.Success;
				window.localStorage.setItem('tenantsso', JSON.stringify(currentUserPool));
			});
		} catch (error) {
			runInAction(() => {
				window.localStorage.setItem('tenantsso', 'false');
				this.userPoolsStatus = Status.Failure;
			});
		}
	}

	@action
	public setupUserPool(currentUserPool: TenantUserPoolType | undefined) {
		let currentPoolId = process.env.REACT_APP_COGNITO_POOL_ID as string;
		let cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID as string;
		let locationOrigin = document.location.origin;
		if (currentUserPool) {
			mutateAppRoutes(currentUserPool.postfix);
			currentPoolId = currentUserPool.userPoolId;
			cognitoClientId = currentUserPool.cognitoClientId;
			locationOrigin = `${document.location.origin}/${currentUserPool.postfix}`;
		}

		Amplify.configure({
			Auth: {
				mandatorySignIn: true,
				region: process.env.REACT_APP_COGNITO_REGION,
				userPoolId: currentPoolId,
				userPoolWebClientId: cognitoClientId,
				storage: localStorage,
				oauth: {
					redirectSignIn: locationOrigin,
					redirectSignOut: locationOrigin,
					responseType: 'token',
					urlOpener: (url: string) => {
						if (this.idleLogout) {
							const w = window.open(url, '_blank');
							setTimeout(() => {
								if (w) {
									w.close();
								}
							}, 10);

							return;
						}
						const windowProxy = window.open(url, '_self');
						if (windowProxy) {
							return Promise.resolve(windowProxy);
						} else {
							return Promise.reject();
						}
					}
				}
			}
		});

		this.currentUserPoolData = currentUserPool ?? {
			userPoolId: currentPoolId,
			cognitoClientId: cognitoClientId,
			postfix: APP_ROUTES.HOME
		};
	}
	@action
	setIdleLogout(value: boolean) {
		this.idleLogout = value;
		this.status.getCurrentUser = Status.Idle;
	}

	@action
	async getAuthSession() {
		try {
			// This method will automatically refresh the accessToken and idToken
			// if tokens are expired and a valid refreshToken presented
			const authSession = await Auth.currentSession();
			runInAction(() => {
				this.authSession = authSession;
			});

			return authSession;
		} catch (e) {
			throw e;
		}
	}

	@action
	async fetchOAuthToken (skipStatusUpdate = false) {
		const oAuthUrl = authGetTokenUrl(
			resolvePeakAuthEnvForToken(window.location.href, window.location.host)
		);
		const formData = new FormData();
		if (this.currentUser?.peakV3Id) {
			localStorage.setItem('peakid', this.currentUser.peakV3Id);
			formData.append("peakid", this.currentUser.peakV3Id);
		}

		// Consume the justLoggedIn flag only when this is the final checkAuth
		// call — i.e. not an intermediate one during T&C / 2FA steps
		// (those pass skipStatusUpdate=true). Also require consent to be given,
		// so the capture happens only when the user has fully completed login.
		const isFullLoginComplete =
			this.justLoggedIn &&
			!skipStatusUpdate &&
			this.currentUser?.isConsentGiven === true;

		if (isFullLoginComplete) {
			this.justLoggedIn = false;
		}

		// Track the in-flight request so callers (see `ensureOAuthToken`) can
		// await a ready token instead of racing an empty value. The existing
		// token is no longer blanked up front — it's only overwritten once the
		// new one arrives, so concurrent requests keep sending the previous
		// valid token during a refresh rather than an empty string.
		this.oauthTokenPromise = axios.post(oAuthUrl, formData)
			.then((response: any) => {
				localStorage.setItem('oauth_token', response.data);
				// Only capture fingerprint once the user has fully logged in and
				// consented — not on page refreshes, T&C steps, 2FA, or token renewals.
				if (isFullLoginComplete) {
					this._captureFingerprint(response.data);
				}
			})
			.catch(function (response: any) {
				console.log(response);
			});

		await this.oauthTokenPromise;
	}

	/**
	 * Resolves once a usable OAuth token is present in localStorage, fetching
	 * one only if we don't already have it. Used to gate LMS / Training-Manager
	 * requests so they never fire with an empty Authorization header while the
	 * initial token fetch (kicked off by `checkAuth`) is still in flight.
	 */
	async ensureOAuthToken (): Promise<string> {
		const existing = localStorage.getItem('oauth_token');
		if (existing) {
			return existing;
		}
		// Reuse an in-flight fetch rather than starting a competing request,
		// so N parallel LMS calls share a single `gettoken` round-trip.
		if (this.oauthTokenPromise) {
			await this.oauthTokenPromise;
		} else {
			await this.fetchOAuthToken();
		}
		return localStorage.getItem('oauth_token') ?? '';
	}

	@action
	async checkAuth(skipStatusUpdate = false) {
		if (!skipStatusUpdate) {
			this.status.checkAuth = Status.Pending;
		}
		try {
			// This method will automatically refresh the accessToken and idToken
			// if tokens are expired and a valid refreshToken presented
			await this.getAuthSession();
			await this.getCurrentUser();
			runInAction(() => {
				this.status.checkAuth = Status.Success;
				const peakV3Id = this.currentUser?.peakV3Id;
				this.twoFactorAuthorizationPassed = Boolean(
					localStorage.getItem(TWO_FACTOR_PREFIX + peakV3Id)
				);
			});
			if(this.isAuthenticated) {
				await this.fetchOAuthToken(skipStatusUpdate);
			}
		} catch (e) {
			runInAction(() => {
				this.status.checkAuth = Status.Failure;
			});
		}
	}

	@action
	async getCurrentUser() {
		if(this.status.getCurrentUser === Status.Pending) return;
		runInAction(() => {
			this.status.getCurrentUser = Status.Pending;
		});
		try {
			const response = await apolloClient.query<GetCurrentUserResponse>({
				query: GET_CURRENT_USER,
				fetchPolicy: 'no-cache'
			});

			const user = response.data.getCurrentUser;
			const permissions = user.primarySystemRole.permissions;

			const hasMappingPermission = permissions.some(
				(p) => p.code === PermissionCode.ViewMappingTenant
			);

			const isSuperAdmin =
				user.primarySystemRole.role === SystemRoleType.SuperAdmin;

			if (hasMappingPermission && !isSuperAdmin) {
				const values = DEFAULT_TABLE_REQUEST_VALUES;
				const variables: GetGroupsListRequest = {
					sort: values.sort,
					filters: {
					...values.filters,
					depth: 1,
					parentId: null
					}
				};
				const groupResp: any = await apolloClient.query<GetGroupsListResponse>({
					query: GET_GROUPS_LIST,
					variables,
					fetchPolicy: "no-cache"
				});

				const groups = groupResp?.data?.listGroups ?? [];
				const hasLevel1 = groups.some((g: any) => g.level === 1);
			    user.isTenantRole = hasLevel1;
				if(user?.primarySystemRole.role === SystemRoleType.SuperAdmin) {
					user.isTenantRole = true;
				}

				if (!hasLevel1) {
					user.primarySystemRole.permissions =
						permissions.filter((p) => p.code !== PermissionCode.ViewMappingTenant);
				}
			}

			runInAction(() => {
				this.currentUser = user;
				this.status.getCurrentUser = Status.Success;

				if (
					this.currentUser.userPool.userPoolId !==
					this.currentUserPoolData?.userPoolId
				) {
					const currentUserPool = this.userPools.find(
						(pool) =>
							pool.userPoolId ===
							this.currentUser?.userPool.userPoolId
					);
					this.prevPostfix = this.currentUserPoolData?.postfix ?? '';
					this.needReloadPostfix = true;
					this.setupUserPool(currentUserPool);
				}
			});
		} catch (e) {
			runInAction(() => {
				this.status.getCurrentUser = Status.Failure;
			});
		}
	}

	@action
	async resetExpiredUser(params: ResetExpiredUserRequest) {
		await axiosClient.post(AUTH_API.RESET_EXPIRED_USER, params);
	}

	@action
	async signIn(data: SignInRequest): Promise<AuthUser> {
		try {
			const user = await Auth.signIn(data.login, data.password);
			this.justLoggedIn = true;
			return user;
		} catch (e) {
			throw e;
		}
	}

	@action
	public resetNeedReloadPostfix() {
		this.needReloadPostfix = false;
	}

	@action
	async resetPassword(data: ResetPasswordRequest) {
		await Auth.forgotPassword(data.login);
	}

	@action
	async resetPasswordSubmit(data: ResetPasswordSubmitRequest) {
		try {
			await Auth.forgotPasswordSubmit(
				data.login,
				data.securityCode,
				data.password
			);
		} catch (e) {
			throw e;
		}
	}

	@action
	async completeNewPassword(data: CompleteNewPasswordRequest) {
		try {
			await Auth.completeNewPassword(data.cognitoUser, data.password);
		} catch (e) {
			throw e;
		}
	}

	@action
	async changePassword(data: ChangePasswordRequest) {
		try {
			const cognitoUser = await Auth.currentAuthenticatedUser();
			await Auth.changePassword(
				cognitoUser,
				data.oldPassword,
				data.password
			);
		} catch (e) {
			throw e;
		}
	}

	@action
	async signOut(triggered = true) {
		const peakV3Id = this.currentUser?.peakV3Id;
		await Auth.signOut();
		runInAction(() => {
			this.authSession = null;
			this.currentUser = null;
			this.status.getCurrentUser = Status.Idle;
			this.twoFactorAuthorizationPassed = false;
		});
		localStorage.removeItem(TWO_FACTOR_PREFIX + peakV3Id);

		if (triggered) {
			this.channel?.postMessage(BROADCAST_CHANNEL_MESSAGE.SIGNOUT);
		}
	}

	@action
	setTwoFactorAuthorizationPassed() {
		this.twoFactorAuthorizationPassed = true;
		const peakV3Id = this.currentUser?.peakV3Id;
		localStorage.setItem(
			TWO_FACTOR_PREFIX + peakV3Id,
			new Date().toISOString()
		);
	}

	/**
	 * Fire-and-forget: load the Fingerprint.com JS SDK, get a requestId + visitorId,
	 * and POST them to ODS /user-ip-data. ODS calls the Fingerprint.com Server API,
	 * stores ip_address, user_agent, and the full event JSON in user_fingerprints.
	 *
	 * - Never blocks the login flow — all errors are swallowed silently.
	 * - oauth_token is sent as Bearer so ODS can verify the user identity server-side.
	 * - user_id is never sent from the browser — ODS extracts it from the JWT.
	 * - Requires three env vars: REACT_APP_FINGERPRINT_URL, REACT_APP_FINGERPRINT_PUBLIC_KEY,
	 *   REACT_APP_ODS_FINGERPRINT_URL (must point to /api/v1/user-ip-data).
	 */
	private _captureFingerprint(oauthToken: string): void {
		const fpUrl  = process.env.REACT_APP_FINGERPRINT_URL;
		const fpKey  = process.env.REACT_APP_FINGERPRINT_PUBLIC_KEY;
		const odsUrl = process.env.REACT_APP_ODS_FINGERPRINT_URL;

		if (!fpUrl || !fpKey || !odsUrl || !oauthToken) return;

		import(/* webpackIgnore: true */ `${fpUrl}/web/v3/${fpKey}`)
			.then((FingerprintJS: any) => FingerprintJS.load({
				endpoint: [fpUrl, (FingerprintJS as any).defaultEndpoint],
			}))
			.then((fp: any) => fp.get())
			.then((result: any) => {
				axios.post(
					odsUrl,
					{ request_id: result.requestId, visitor_id: result.visitorId },
					{ headers: { Authorization: `Bearer ${oauthToken}` } },
				).catch(() => { /* silent — fingerprint capture must never surface errors */ });
			})
			.catch(() => { /* silent */ });
	}

	@action
	private _filterUserPermission = async (featureList: FEATURE_TYPE[]) => {
		if (!featureList.length || !this.currentUser) return;
		const userRole = this.currentUser?.primarySystemRole.role;
		if(this.currentUser) {
			if(userRole === SystemRoleType.User) return;
		}
	
		const disabledCodes = new Set<string>();
		const enabledCodes = new Set<string>();

		for (const { featureName, isEnabled } of featureList) {
			const permissions = FEATURE_PERMISSION_KEYS[featureName] ?? [];

			for (const code of permissions) {
				(isEnabled ? enabledCodes : disabledCodes).add(code);
			}
		}
	
		runInAction(() => {
			// Remove permissions for disabled features
			let permissions = (this.currentUser!.primarySystemRole.permissions || []);
			const rootTenant = systemsStore.rootTenant.value;
			const isTenantLevelAdmin = userRole === SystemRoleType.SuperAdmin ? true : (userRole === SystemRoleType.Admin && rootTenant === this.currentUser?.groups?.[0]?.id);
			if(isTenantLevelAdmin) {
				permissions.push({ code: PermissionCode.VIEW_FEATURE_MANAGER, id: Math.floor(Math.random() * 999)})
			}

			if(disabledCodes.size) permissions = permissions.filter(({ code }) => !disabledCodes.has(code));

			if (enabledCodes.size) {
				const existingCodes = new Set(permissions.map(({ code }) => code));

				enabledCodes.forEach(permissionCode => {
					const code = permissionCode as PermissionCode;
					if(!existingCodes.has(code)) permissions.push({ code, id: Math.floor(Math.random() * 999)})
				});
			}	
			this.currentUser!.primarySystemRole.permissions = permissions;
		});
	}
}

export const authStore = new AuthStore();
export default AuthStore;
